// SF4 — an ASIN in, a costed targeting plan out.
//
// The column that matters, and that every keyword tool leaves out, is "you can
// afford". Amazon's suggested bid says what winning the auction costs. The
// affordable bid says what this product can pay and still meet target. Where the
// second is below the first, the row says so rather than selling the seller a
// keyword they cannot make money on.
import React, { useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import s from '../KeywordOptimiser.module.css';
import { AFFORD_CLASS, money, num, pct, rupees2 } from '../format';

function Demand({ demand }) {
  if (!demand) return <span className={s.thin}>—</span>;
  if (demand.volume) {
    return <>{num(demand.volume)}<div className={s.thin}>searches a month</div></>;
  }
  if (demand.rank) {
    return <>#{num(demand.rank)}<div className={s.thin}>search rank, not a count</div></>;
  }
  return <span className={s.thin} title={demand.note}>not available</span>;
}

function Rows({ rows, currency }) {
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th>Keyword</th><th>Why it is here</th><th className={s.right}>Demand</th>
            <th className={s.right}>Your share</th><th className={s.right}>Amazon bid</th>
            <th className={s.right}>You can afford</th><th>Match</th>
            <th>In listing</th><th>Expected a month</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.keyword}>
              <td>
                <span className={s.term}>{r.keyword}</span>
                {r.title && <div className={s.thin}>{String(r.title).slice(0, 48)}</div>}
                {r.price_comparison && (
                  <div className={s.thin}>{r.price_comparison}</div>
                )}
                {r.policy_note && <div className={s.sugWarn}>{r.policy_note}</div>}
              </td>
              <td className={s.evidence}>{r.evidence_reads}</td>
              <td className={s.right}><Demand demand={r.demand} /></td>
              <td className={s.right}>
                {r.your_share?.click_share != null
                  ? pct(r.your_share.click_share, 1)
                  : <span className={s.thin}>{r.your_share?.note || '—'}</span>}
              </td>
              <td className={s.right}>
                {r.competition?.bid_low != null
                  ? `${rupees2(r.competition.bid_low, currency)}–${rupees2(r.competition.bid_high, currency)}`
                  : <span className={s.thin}>—</span>}
              </td>
              <td className={s.right}>
                {r.affordable_bid != null
                  ? <span className={s[AFFORD_CLASS[r.affordability] || 'afford_unknown']}>
                      {rupees2(r.affordable_bid, currency)}
                    </span>
                  : <span className={s.afford_unknown}>{r.affordability}</span>}
                {r.affordability_reason && (
                  <div className={s.thin}>{r.affordability_reason}</div>
                )}
              </td>
              <td>
                {r.suggested_bid != null
                  ? <>{r.match_type.toLowerCase()}<div className={s.thin}>
                      at {rupees2(r.suggested_bid, currency)}</div></>
                  : r.match_type.toLowerCase()}
              </td>
              <td className={s.thin}>
                {r.in_listing === null ? '—'
                  : r.in_listing ? 'yes'
                    : `missing: ${(r.missing_words || []).slice(0, 3).join(', ')}`}
              </td>
              <td className={s.thin}>
                {r.expected?.available
                  ? `${num(r.expected.clicks_low)}–${num(r.expected.clicks_high)} clicks, `
                    + `${num(r.expected.orders_low)}–${num(r.expected.orders_high)} orders`
                    + (r.expected.sales_low
                      ? `, ${money(r.expected.sales_low, currency)}–${money(r.expected.sales_high, currency)}`
                      : '')
                  : (r.expected?.note || '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Discovery({ plan, loading, onDiscover, busy }) {
  const [asins, setAsins] = useState('');

  return (
    <div>
      <div className={s.discoverBar}>
        <label className={s.field} style={{ flex: '1 1 260px' }}>
          <span className={s.label}>Your ASINs, comma separated</span>
          <input className={s.asinInput} value={asins} placeholder="B089BGVCPN, B0…"
                 onChange={(e) => setAsins(e.target.value)} />
        </label>
        <button className={s.button} disabled={busy || !asins.trim()}
                onClick={() => onDiscover(asins.split(',').map((a) => a.trim())
                  .filter(Boolean))}>
          <FiSearch /> Build a plan
        </button>
      </div>
      <p className={s.coverage}>
        Built only from Amazon: its own keyword and product recommendations, Search
        Query Performance, the Brand Analytics search terms report, and your own search
        term data. Nothing is estimated where Amazon is silent.
      </p>

      {loading && <p className={s.empty}>Asking Amazon…</p>}
      {!loading && !plan && (
        <p className={s.empty}>
          Give it one of your products and it will come back with what to target, what
          each keyword costs, what you can afford, and what to expect.
        </p>
      )}

      {plan && (
        <>
          <div className={s.stats}>
            <div className={s.stat}>
              <span className={s.statValue}>{plan.total}</span>
              <span className={s.statLabel}>worth considering</span>
            </div>
            <div className={s.stat}>
              <span className={s.statValue}>
                {(plan.rows || []).filter((r) =>
                  r.affordability === 'profitable at a competitive bid').length}
              </span>
              <span className={s.statLabel}>profitable at a competitive bid</span>
            </div>
            <div className={s.stat}>
              <span className={s.statValue}>
                {(plan.rows || []).filter((r) => r.in_listing === false).length}
              </span>
              <span className={s.statLabel}>not in your listing</span>
            </div>
          </div>
          <p className={s.coverage}>{plan.evidence_note}</p>
          {plan.dropped_note && <p className={s.coverage}>{plan.dropped_note}</p>}
          {(plan.notes || []).map((note) => (
            <p key={note} className={s.coverageWarn}>{note}</p>
          ))}

          {Object.entries(plan.groups || {}).map(([name, rows]) => (
            <div key={name}>
              <h3 className={s.groupTitle}>
                {name}<span className={s.groupCount}>{rows.length}</span>
              </h3>
              <Rows rows={rows} currency={plan.currency} />
            </div>
          ))}
        </>
      )}
    </div>
  );
}
